import { Preferences } from '@capacitor/preferences';

const REMINDERS_KEY = 'local_reminders';
const DEFAULTS_SEEDED_KEY = 'local_reminders_defaults_seeded';

export type ReminderCategory = 'food' | 'symptom' | 'supplement' | 'meditation' | 'custom';
export type DayOfWeek = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';
export type ScheduleType = 'daily' | 'weekly' | 'interval';

export type ReminderSchedule = {
  type: ScheduleType;
  times?: string[];         // 'HH:MM' strings used by daily and weekly
  days?: DayOfWeek[];       // day-of-week filter for weekly
  every_minutes?: number;   // interval cadence
};

export type LocalReminder = {
  id: string;
  category: ReminderCategory;
  title_key: string;
  body_key: string;
  body_params?: Record<string, string>;
  schedule: ReminderSchedule;
  enabled: boolean;
  deep_link?: string;
  last_fired_at?: string;
};

function newId(): string {
  return (
    globalThis.crypto?.randomUUID?.() ??
    `${Date.now()}-${Math.random().toString(36).slice(2)}`
  );
}

async function loadAll(): Promise<LocalReminder[]> {
  const { value } = await Preferences.get({ key: REMINDERS_KEY });
  if (!value) return [];
  try {
    return JSON.parse(value) as LocalReminder[];
  } catch {
    return [];
  }
}

async function saveAll(reminders: LocalReminder[]): Promise<void> {
  await Preferences.set({ key: REMINDERS_KEY, value: JSON.stringify(reminders) });
}

export async function getReminders(): Promise<LocalReminder[]> {
  return loadAll();
}

export async function addReminder(
  data: Omit<LocalReminder, 'id'>,
): Promise<LocalReminder> {
  const all = await loadAll();
  const reminder: LocalReminder = { ...data, id: newId() };
  await saveAll([...all, reminder]);
  return reminder;
}

export async function updateReminder(
  id: string,
  patch: Partial<Omit<LocalReminder, 'id'>>,
): Promise<LocalReminder | null> {
  const all = await loadAll();
  const idx = all.findIndex((r) => r.id === id);
  if (idx === -1) return null;
  const updated: LocalReminder = { ...all[idx], ...patch };
  all[idx] = updated;
  await saveAll(all);
  return updated;
}

export async function deleteReminder(id: string): Promise<boolean> {
  const all = await loadAll();
  const next = all.filter((r) => r.id !== id);
  if (next.length === all.length) return false;
  await saveAll(next);
  return true;
}

export async function markFired(id: string): Promise<void> {
  await updateReminder(id, { last_fired_at: new Date().toISOString() });
}

const DEFAULT_REMINDERS: Array<Omit<LocalReminder, 'id'>> = [
  {
    category: 'food',
    title_key: 'reminders.copy.food.title',
    body_key: 'reminders.copy.food.body',
    schedule: { type: 'daily', times: ['20:00'] },
    enabled: false,
    deep_link: '/app/log/food',
  },
  {
    category: 'symptom',
    title_key: 'reminders.copy.symptom.title',
    body_key: 'reminders.copy.symptom.body',
    schedule: { type: 'daily', times: ['21:00'] },
    enabled: false,
    deep_link: '/app/log/symptom',
  },
  {
    category: 'supplement',
    title_key: 'reminders.copy.supplement.title',
    body_key: 'reminders.copy.supplement.body',
    schedule: { type: 'daily', times: ['08:00', '20:00'] },
    enabled: false,
    deep_link: '/app/log/supplement',
  },
];

// Seeds the three default reminders once, all disabled, so users opt-in.
export async function ensureDefaultReminders(): Promise<void> {
  const { value } = await Preferences.get({ key: DEFAULTS_SEEDED_KEY });
  if (value === 'true') return;
  const existing = await loadAll();
  if (existing.length === 0) {
    for (const def of DEFAULT_REMINDERS) {
      await addReminder(def);
    }
  }
  await Preferences.set({ key: DEFAULTS_SEEDED_KEY, value: 'true' });
}
